import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

// A numbered manifesto chapter, alternating layout image left/right.
// Includes: chapter number, overline label, big heading, description, bullet list, visualization slot.
export default function Chapter({
  index,
  overline,
  title,
  description,
  bullets = [],
  visual,
  reverse = false,
  anchor,
  cta,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // subtle parallax: image slides at 5%
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <section
      id={anchor}
      ref={ref}
      data-testid={`chapter-${index}`}
      className="relative py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Chapter marker row */}
        <div className="flex items-center gap-4 mb-10 md:mb-14">
          <span className="font-mono-bp text-[11px] tracking-[0.35em] uppercase text-[#00FAF8]">
            Kapitel {index}
          </span>
          <span className="flex-1 h-px bg-gradient-to-r from-[#00FAF8]/40 via-white/10 to-transparent" />
          <span className="font-mono-bp text-[11px] tracking-[0.25em] uppercase text-white/40">
            {overline}
          </span>
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center ${
            reverse ? "" : ""
          }`}
        >
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className={`lg:col-span-6 xl:col-span-5 ${
              reverse ? "lg:order-2 lg:col-start-8 xl:col-start-8" : ""
            }`}
          >
            {/* huge number */}
            <div className="font-display font-light text-[96px] md:text-[140px] leading-[0.85] text-white/[0.06] tracking-tighter -mb-4">
              {index}
            </div>
            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.03em] font-medium">
              {title}
            </h2>
            <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-xl">
              {description}
            </p>

            {bullets.length > 0 && (
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                {bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2.5 text-sm text-white/75"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#00FAF8] shadow-[0_0_10px_rgba(0,250,248,0.7)]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            {cta && (
              <a
                href={cta.href}
                data-testid={`chapter-${index}-cta`}
                className="mt-10 inline-flex items-center gap-2 text-sm text-white/85 bp-link"
              >
                {cta.label}
                <ArrowUpRight size={16} className="text-[#00FAF8]" />
              </a>
            )}
          </motion.div>

          {/* Visual */}
          <motion.div
            style={{ y }}
            className={`lg:col-span-6 xl:col-span-7 ${
              reverse ? "lg:order-1 lg:col-start-1" : "lg:col-start-7 xl:col-start-6"
            }`}
          >
            <div className="relative">{visual}</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
